<script lang="ts">
  import { slide } from "svelte/transition";
  import { page } from "$app/stores";
  import {
    locations,
    departments,
    selectedCareersLocations,
    selectedCareersDepartments,
    jobsSearch,
    careersMobileFiltersOpen,
  } from "$stores";
  import FilterItem from "./FilterItem.svelte";
  import { onDestroy, onMount } from "svelte";

  export let isMobileVersion: boolean = false;

  let locationsOpen = false;
  let departmentsOpen = false;

  onMount(() => {
    const urlLoc = $page.url.searchParams.get("loc");
    if (urlLoc) {
      const splitReverse = urlLoc.split(", ").reverse();
      if (!splitReverse.length) return;
      selectedCareersLocations.set([
        {
          name: splitReverse[0],
          children: splitReverse[1]
            ? [
                {
                  name: splitReverse[1],
                  children: splitReverse[2]
                    ? [{ name: splitReverse[2], children: [] }]
                    : [],
                },
              ]
            : [],
        },
      ]);

      locationsOpen = true;
      departmentsOpen = false;
    }

    const depLoc = $page.url.searchParams.get("dep");
    if (depLoc) {
      selectedCareersDepartments.set([depLoc]);
      locationsOpen = false;
      departmentsOpen = true;
    }
  });

  function clearFilters() {
    selectedCareersLocations.set([]);
    selectedCareersDepartments.set([]);
    jobsSearch.set("");
    locationsOpen = false;
    departmentsOpen = false;
  }

  onDestroy(() => {
    clearFilters();
  });
</script>

<div class="filters">
  <div class="flex justify-between items-center mb-8">
    <div class="text-[28px] type:h4">Filter by</div>
    {#if isMobileVersion}
      <svg
        tabindex="-1"
        role="button"
        on:click={() => {
          careersMobileFiltersOpen.set(false);
        }}
        on:keydown={() => {
          careersMobileFiltersOpen.set(false);
        }}
        class="!h-[36px] !w-[36px] group transition-all duration-150 cursor-pointer"
        viewBox="0 0 36 36"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        ><circle
          class="group-hover:fill-[#1d1d1c] transition-all duration-150"
          cx="18"
          cy="18"
          r="18"
          fill="#fff"
        /><mask
          id="a"
          style="mask-type:alpha"
          maskUnits="userSpaceOnUse"
          x="6"
          y="6"
          width="24"
          height="24"
          ><path
            class="group-hover:fill-[#fff] transition-all duration-150"
            fill="#D9D9D9"
            d="M6 6H30V30H6z"
          /></mask
        ><g mask="url(#a)"
          ><path
            class="group-hover:fill-[#fff] transition-all duration-150"
            d="M12.4 25L11 23.6l5.6-5.6-5.6-5.6 1.4-1.4 5.6 5.6 5.6-5.6 1.4 1.4-5.6 5.6 5.6 5.6-1.4 1.4-5.6-5.6-5.6 5.6z"
            fill="#1C1B1F"
          /></g
        ></svg
      >
    {:else}
      <div
        class="select-none cursor-pointer text-black type:body-small"
        tabindex="-10"
        role="button"
        on:click={clearFilters}
        on:keydown={clearFilters}
      >
        Clear filters
      </div>
    {/if}
  </div>

  <div>
    <!-- Locations -->
    <section>
      <div
        class="filter-tab-level-1 !pb-0"
        tabindex="-10"
        role="button"
        on:click={() => {
          locationsOpen = !locationsOpen;
        }}
        on:keydown={() => {
          locationsOpen = !locationsOpen;
        }}
      >
        <div>Location</div>
        <div style="transform:{locationsOpen ? '' : 'rotate(180deg)'}">
          <svg
            class="w-[14px] h-[9px]"
            viewBox="0 0 14 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            ><path
              d="M1.703 7.322l5.648-5.643 5.647 5.643"
              stroke="#242021"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            /></svg
          >
        </div>
      </div>

      {#if locationsOpen}
        <div class="pt-1" transition:slide|global={{ duration: 300 }}>
          {#each $locations as first, i}
            <!-- Level 1 -->
            <div class="">
              <FilterItem
                type="locations"
                level={1}
                name={first.name}
                full={first.full}
                hasChildren={first.children.length > 0}
                isLast={$locations.length === i + 1}
                store={selectedCareersLocations}
              >
                {#if first.children.length > 0}
                  <div class="pl-6 mt-2">
                    {#each first.children as second}
                      <FilterItem
                        type="locations"
                        level={2}
                        name={second.name}
                        full={second.full}
                        store={selectedCareersLocations}
                      >
                        {#if second.children.length > 0}
                          <div class="pl-6">
                            {#each second.children as third}
                              <FilterItem
                                type="locations"
                                level={3}
                                name={third.name}
                                full={third.full}
                                store={selectedCareersLocations}
                              />
                            {/each}
                          </div>
                        {/if}
                      </FilterItem>
                    {/each}
                  </div>
                {/if}
              </FilterItem>
            </div>
          {/each}
        </div>
      {/if}
    </section>

    <!-- Departments -->
    <section class="pt-4">
      <div
        class="filter-tab-level-1"
        tabindex="-10"
        role="button"
        on:click={() => {
          departmentsOpen = !departmentsOpen;
        }}
        on:keydown={() => {
          departmentsOpen = !departmentsOpen;
        }}
      >
        <div>Departments</div>
        <div style="transform:{departmentsOpen ? '' : 'rotate(180deg)'}">
          <svg
            class="w-[14px] h-[9px]"
            viewBox="0 0 14 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            ><path
              d="M1.703 7.322l5.648-5.643 5.647 5.643"
              stroke="#242021"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            /></svg
          >
        </div>
      </div>

      {#if departmentsOpen}
        <div transition:slide|global={{ duration: 300 }}>
          {#each $departments as l}
            <div class="mb-2">
              <FilterItem
                type="departments"
                name={l}
                store={selectedCareersDepartments}
              />
            </div>
          {/each}
        </div>
      {/if}
    </section>
  </div>
</div>

<style lang="scss">
  .filter-tab-level-1 {
    @apply py-4 text-[18px] font-semibold text-[#242021] flex justify-between items-center cursor-pointer;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
</style>
